import type { ListMode } from './ListContext';
import type { List, ListEvent } from '@certhon/models';

// ---------------------------------------------------------------------------
// Module-level shared store, keyed by listId.
//
// Multiple ListProvider instances that reference the same listId will
// share the same pending-events queue and mode override.  This is necessary
// because the Stack layout renders drill-down panels as React siblings, so
// context from a parent panel does NOT flow to a child panel.
// ---------------------------------------------------------------------------

type Listener = () => void;

export interface ChecklistStoreState {
  pendingEvents: ListEvent[];
  modeOverride: ListMode | null;
  /**
   * Lists injected client-side ahead of a pending EXPAND_PROTO event so the
   * reducer can resolve the new dependency before the server has been told
   * about it. Keyed by list id; merged into `allLists` by ChecklistContext.
   *
   * Populated by AddGroupMenu's "Expand template" action (and any other UI
   * that queues an EXPAND_PROTO in edit mode for a template not already in
   * the list's dependencies). Cleared on revert/commit because by then the
   * authoritative server state will include the deps.
   */
  injectedLists: Record<string, List>;
}

interface StoreEntry {
  state: ChecklistStoreState;
  listeners: Set<Listener>;
  refCount: number;
}

const stores = new Map<string, StoreEntry>();

function getOrCreate(listId: string): StoreEntry {
  let entry = stores.get(listId);
  if (!entry) {
    entry = {
      state: { pendingEvents: [], modeOverride: null, injectedLists: {} },
      listeners: new Set(),
      refCount: 0,
    };
    stores.set(listId, entry);
  }
  return entry;
}

function notify(entry: StoreEntry) {
  for (const listener of entry.listeners) {
    listener();
  }
}

// ---------------------------------------------------------------------------
// Public API consumed by ListProvider
// ---------------------------------------------------------------------------

// FIXME: subscribe calls getOrCreate which can resurrect a store entry
// during the 200ms cleanup window after all providers unmount. In practice
// this is unlikely since new providers call addRef in the same render cycle.
export function subscribe(listId: string, listener: Listener): () => void {
  const entry = getOrCreate(listId);
  entry.listeners.add(listener);
  return () => {
    entry.listeners.delete(listener);
  };
}

export function getSnapshot(listId: string): ChecklistStoreState {
  return getOrCreate(listId).state;
}

export function addRef(listId: string): void {
  getOrCreate(listId).refCount++;
}

export function releaseRef(listId: string): void {
  const entry = stores.get(listId);
  if (!entry) return;
  entry.refCount--;
  if (entry.refCount <= 0) {
    // Defer cleanup to allow sibling panels to mount and re-acquire a ref.
    // During drill-down navigation the parent panel unmounts before the child
    // panel mounts, which briefly drops the refCount to 0.  Immediate deletion
    // would discard shared state (e.g. modeOverride) that the child still needs.
    setTimeout(() => {
      const current = stores.get(listId);
      if (current && current.refCount <= 0) {
        stores.delete(listId);
      }
    }, 200);
  }
}

export function setPendingEvents(
  listId: string,
  updater: ListEvent[] | ((prev: ListEvent[]) => ListEvent[]),
): void {
  const entry = getOrCreate(listId);
  const prev = entry.state.pendingEvents;
  const next = typeof updater === 'function' ? updater(prev) : updater;
  // Always produce a new state object so useSyncExternalStore triggers a re-render
  entry.state = { ...entry.state, pendingEvents: next };
  notify(entry);
}

export function setModeOverride(listId: string, mode: ListMode | null): void {
  const entry = getOrCreate(listId);
  entry.state = { ...entry.state, modeOverride: mode };
  notify(entry);
}

/**
 * Inject one-or-more lists (a template plus its transitive dependencies)
 * into the per-listId store. Used by EXPAND_PROTO call sites that queue an
 * event referencing a template not yet in the list's `allLists`. Idempotent:
 * if a list with the same id is already injected it is replaced.
 */
export function addInjectedLists(listId: string, lists: List[]): void {
  if (lists.length === 0) return;
  const entry = getOrCreate(listId);
  const next = { ...entry.state.injectedLists };
  for (const l of lists) next[l.id] = l;
  entry.state = { ...entry.state, injectedLists: next };
  notify(entry);
}

/** Drop all injected lists. Called on revert/commit. */
export function clearInjectedLists(listId: string): void {
  const entry = stores.get(listId);
  if (!entry) return;
  if (Object.keys(entry.state.injectedLists).length === 0) return;
  entry.state = { ...entry.state, injectedLists: {} };
  notify(entry);
}
